import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { API_URL } from '@/config/api';

const ReferralForm: React.FC = () => {
    const [formData, setFormData] = useState({
        referrerEmail: '',
        friendEmail: '',
    });

    const [status, setStatus] = useState({
        loading: false,
        message: '',
        type: '', // 'success' | 'error'
    });

    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [recaptchaToken, setRecaptchaToken] = useState("");

    // OTP State
    const [isVerified, setIsVerified] = useState(false);
    const [otpMode, setOtpMode] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpStatus, setOtpStatus] = useState({ loading: false, message: "", type: "" });

    const handleSendOTP = async () => {
        if (!formData.referrerEmail) {
            setOtpStatus({ loading: false, message: "Please enter your email.", type: "error" });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.referrerEmail)) {
            setOtpStatus({ loading: false, message: "Invalid email address.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/request-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ identifier: formData.referrerEmail, context: "contact" })
            });

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setOtpMode(true);
                setOtpStatus({ loading: false, message: "Code sent to your email.", type: "success" });
            } else {
                setOtpStatus({ loading: false, message: data.message || "Failed to send code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Send Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    const handleVerifyOTP = async () => {
        if (!otp || otp.length < 6) {
            setOtpStatus({ loading: false, message: "Enter 6-digit code.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.referrerEmail, otp, identifier: formData.referrerEmail })
            });

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setIsVerified(true);
                setOtpMode(false);
                setOtpStatus({ loading: false, message: "Verified!", type: "success" });
                setTimeout(() => setOtpStatus({ loading: false, message: "", type: "" }), 3000);
            } else {
                setOtpStatus({ loading: false, message: data.message || "Invalid code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Verify Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.referrerEmail === formData.friendEmail) {
            setStatus({
                loading: false,
                message: "Referrer and friend emails cannot be the same.",
                type: 'error'
            });
            return;
        }

        if (!isVerified) {
            setStatus({ loading: false, message: "Please verify your email address before submitting.", type: "error" });
            return;
        }

        if (!recaptchaToken) {
            setStatus({ loading: false, message: "Please complete reCAPTCHA.", type: "error" });
            return;
        }

        setStatus({ loading: true, message: '', type: '' });

        try {
            const response = await fetch(`${API_URL}/referral`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    recaptchaToken
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setStatus({
                    loading: false,
                    message: "Referral submitted successfully! Emails have been sent.",
                    type: 'success'
                });
                setFormData({ referrerEmail: '', friendEmail: '' });
                setRecaptchaToken("");
                setIsVerified(false);
                setOtpMode(false);
                setOtp("");

                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                // Clear success message after 5 seconds
                setTimeout(() => {
                    setStatus(prev => prev.type === 'success' ? { ...prev, message: '' } : prev);
                }, 5000);
            } else {
                throw new Error(data.message || "Failed to submit referral.");
            }
        } catch (error: any) {
            console.error("Referral Error:", error);
            setStatus({
                loading: false,
                message: error.message || "An error occurred. Please try again.",
                type: 'error'
            });
        }
    };

    return (
        <section id="referral-form" className="pb-16 pt-0 mt-4 md:mt-8 bg-transparent">
            <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="flex gap-0 md:gap-6">
                        {/* Empty space to align with the timeline cards above */}
                        <div className="hidden md:block w-16 md:w-20 flex-shrink-0" />
                        <div className="flex-1">
                            <div className="relative bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-xl p-5 md:p-8 rounded-2xl shadow-xl border border-purple-500/20 hover:border-purple-500/50 transition-all duration-400 hover:md:translate-x-3 hover:-translate-y-1 md:hover:-translate-y-0 hover:shadow-2xl group">
                                <div className="text-center mb-10">
                                    <h3 className="text-xl md:text-3xl font-bold text-white mb-4 transition-colors duration-300 group-hover:text-purple-300">Ready to Start Referring?</h3>
                                    <p className="text-sm md:text-base text-gray-300">Fill in the details below to send an invite to your friend.</p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Referrer Email */}
                                        <div className="relative group">
                                            <div className="relative">
                                                <input
                                                    type="email"
                                                    name="referrerEmail"
                                                    value={formData.referrerEmail}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={status.loading || isVerified}
                                                    className="peer w-full px-4 py-4 pr-24 bg-white/5 border border-gray-600 rounded-2xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                                    placeholder="Your Email"
                                                />
                                                <label className="absolute left-4 top-1 text-xs text-gray-400 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-4 peer-placeholder-shown:text-gray-500 peer-focus:top-1 peer-focus:text-xs peer-focus:text-purple-400">
                                                    Your Email Address *
                                                </label>

                                                {/* Action Button / Tick Mark */}
                                                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center pt-2">
                                                    {formData.referrerEmail && !isVerified && !otpMode && (
                                                        <button
                                                            type="button"
                                                            onClick={handleSendOTP}
                                                            disabled={otpStatus.loading}
                                                            className="flex items-center justify-center w-8 h-8 bg-purple-600/20 hover:bg-purple-600 text-purple-400 hover:text-white rounded-lg border border-purple-500/30 transition-all duration-300 disabled:opacity-50"
                                                            title="Verify Email"
                                                        >
                                                            {otpStatus.loading ? (
                                                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                                </svg>
                                                            ) : (
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                    )}
                                                    {(isVerified || otpMode) && (
                                                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 shadow-[0_0_10px_rgba(34,197,94,0.2)] animate-in fade-in zoom-in duration-500">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {otpMode && (
                                                <div className="mt-4 p-4 bg-white/5 border border-purple-500/20 rounded-2xl space-y-4">
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            maxLength={6}
                                                            value={otp}
                                                            onChange={(e) => setOtp(e.target.value)}
                                                            className="peer w-full px-4 py-3 bg-white/5 border border-purple-500/20 rounded-2xl text-white text-center text-2xl tracking-widest focus:outline-none focus:border-purple-500 transition-all"
                                                            placeholder="000000"
                                                        />
                                                        <label className="block text-center text-xs text-gray-400 mt-2 uppercase tracking-wider">
                                                            Enter Code
                                                        </label>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={handleVerifyOTP}
                                                            disabled={otpStatus.loading}
                                                            className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
                                                        >
                                                            {otpStatus.loading ? "Verifying..." : "Confirm"}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setOtpMode(false)}
                                                            className="px-4 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {otpStatus.message && !isVerified && (
                                                <p className={`mt-2 text-sm px-2 ${otpStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                                                    {otpStatus.message}
                                                </p>
                                            )}
                                        </div>

                                        {/* Friend Email */}
                                        <div className="relative">
                                            <input
                                                type="email"
                                                name="friendEmail"
                                                value={formData.friendEmail}
                                                onChange={handleChange}
                                                required
                                                className="peer w-full px-4 py-4 bg-white/5 border border-gray-600 rounded-2xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300"
                                                placeholder="Friend's Email"
                                            />
                                            <label className="absolute left-4 top-1 text-xs text-gray-400 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-4 peer-placeholder-shown:text-gray-500 peer-focus:top-1 peer-focus:text-xs peer-focus:text-purple-400">
                                                Friend's Email Address *
                                            </label>
                                        </div>
                                    </div>

                                    {/* Captcha */}
                                    <div className="flex justify-center flex-col items-center gap-4">
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                                            theme="dark"
                                            onChange={(token) => setRecaptchaToken(token || "")}
                                        />
                                        {!recaptchaToken && (
                                            <p className="text-gray-500 text-xs">Security verification required</p>
                                        )}
                                    </div>

                                    {/* Status Message */}
                                    {status.message && (
                                        <div className={`p-4 rounded-xl text-sm ${status.type === 'success'
                                            ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                                            : 'bg-red-500/10 border border-red-500/30 text-red-400'
                                            }`}>
                                            {status.message}
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <div className="flex flex-col items-center gap-4 pt-4">
                                        <button
                                            type="submit"
                                            disabled={status.loading || !isVerified || !recaptchaToken}
                                            className="px-10 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-full shadow-lg hover:shadow-purple-500/40 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group flex items-center gap-2"
                                        >
                                            {status.loading ? (
                                                <>
                                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Submitting...
                                                </>
                                            ) : (
                                                "Send Referral Now"
                                            )}
                                        </button>
                                        <p className="text-[10px] md:text-xs text-gray-400 max-w-xl text-center leading-relaxed px-4">
                                            A referral bonus will be payable only if the referred client formally signs a contract with the company and the total project value exceeds $1,000. The bonus will be issued after the contract is executed and the project has been confirmed.
                                        </p>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ReferralForm;
